import React, { useState, useEffect, ChangeEvent } from 'react';
import { ApiResponse } from "../../../../services/api";

interface Estado {
    uf: string;
    nome: string;
}

interface Municipio {
    id: number;
    nome: string;
    uf: string;
}

interface ApiService {
    getMunicipios: (filters: { uf: string }) => Promise<ApiResponse<Municipio[]>>;
}

interface LocationSelectorProps {
    selectedState: string | undefined;
    selectedMunicipality: number | undefined;
    onStateChange: (state: string) => void;
    onMunicipalityChange: (municipalityId: string) => void;
    apiService: ApiService;
    isEditing?: boolean;
}

const ESTADOS_BRASIL: Estado[] = [
    { uf: 'AC', nome: 'Acre' },
    { uf: 'AL', nome: 'Alagoas' },
    { uf: 'AP', nome: 'Amapá' },
    { uf: 'AM', nome: 'Amazonas' },
    { uf: 'BA', nome: 'Bahia' },
    { uf: 'CE', nome: 'Ceará' },
    { uf: 'DF', nome: 'Distrito Federal' },
    { uf: 'ES', nome: 'Espírito Santo' },
    { uf: 'GO', nome: 'Goiás' },
    { uf: 'MA', nome: 'Maranhão' },
    { uf: 'MT', nome: 'Mato Grosso' },
    { uf: 'MS', nome: 'Mato Grosso do Sul' },
    { uf: 'MG', nome: 'Minas Gerais' },
    { uf: 'PA', nome: 'Pará' },
    { uf: 'PB', nome: 'Paraíba' },
    { uf: 'PR', nome: 'Paraná' },
    { uf: 'PE', nome: 'Pernambuco' },
    { uf: 'PI', nome: 'Piauí' },
    { uf: 'RJ', nome: 'Rio de Janeiro' },
    { uf: 'RN', nome: 'Rio Grande do Norte' },
    { uf: 'RS', nome: 'Rio Grande do Sul' },
    { uf: 'RO', nome: 'Rondônia' },
    { uf: 'RR', nome: 'Roraima' },
    { uf: 'SC', nome: 'Santa Catarina' },
    { uf: 'SP', nome: 'São Paulo' },
    { uf: 'SE', nome: 'Sergipe' },
    { uf: 'TO', nome: 'Tocantins' }
];

const LocationSelector: React.FC<LocationSelectorProps> = ({
    selectedState,
    selectedMunicipality,
    onStateChange,
    onMunicipalityChange,
    apiService,
    isEditing = false
}) => {
    const [municipios, setMunicipios] = useState<Municipio[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (selectedState) {
            fetchMunicipios(selectedState);
        }
    }, [selectedState]);

    const fetchMunicipios = async (uf: string) => {
        try {
            setLoading(true);
            const response = await apiService.getMunicipios({ uf });
            if (response.success && response.data) {
                setMunicipios(response.data);
            }
        } catch (error) {
            console.error('Erro ao buscar municípios:', error);
            setMunicipios([]);
        } finally {
            setLoading(false);
        }
    };

    const handleStateChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const newState = e.target.value;
        setMunicipios([]); // Limpa os municípios quando o estado muda
        onStateChange(newState);
        onMunicipalityChange(''); // Reseta a seleção do município
    };

    return (
        <>
            <div className="mb-3">
                <label className="col-form-label">Estado</label>
                <select
                    className="form-select"
                    value={selectedState || ''}
                    onChange={handleStateChange}
                    disabled={isEditing && selectedState ? true : false}
                >
                    <option value="">Selecione o estado...</option>
                    {ESTADOS_BRASIL.map(estado => (
                        
                        <option key={estado.uf} value={estado.uf}>
                            {estado.nome} ({estado.uf})
                        </option>

                        
                    ))}
                </select>
            </div>

            <div className="mb-3">
                <label className="col-form-label">Município</label>
                <select
                    className="form-select"
                    value={selectedMunicipality?.toString() || ''}
                    onChange={(e) => onMunicipalityChange(e.target.value)}
                    disabled={!selectedState || loading}
                >
                    <option value="">
                        {loading
                            ? 'Carregando municípios...'
                            : selectedState
                                ? 'Selecione o município...'
                                : 'Selecione primeiro um estado'
                        }
                    </option>
                    {municipios.map(municipio => (
                        <option key={municipio.id} value={municipio.id}>
                            {municipio.nome}
                        </option>
                    ))}
                </select>
            </div>
        </>
    );
};

export default LocationSelector;